import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trophy, Medal, Award, Menu } from 'lucide-react';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { getStatistics, getCompletedBooksCount } from '../utils/localStorage';
import { SideMenu } from './SideMenu';
import { supabase } from '../lib/supabase';
import { getCurrentUser } from '../utils/auth';

export const RankingPage = () => {
  const navigate = useNavigate();
  const localStats = getStatistics();
  const completedBooksCount = getCompletedBooksCount();
  const [menuOpen, setMenuOpen] = useState(false);

  const handlePlayClick = () => navigate('/select-book');

  const [userDisplayName, setUserDisplayName] = useState('Jugador');
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userProfile, setUserProfile] = useState(null);

  const formatName = (fullName) => {
    if (!fullName) return 'Jugador';
    const parts = fullName.trim().split(/\s+/);
    if (parts.length > 1) return `${parts[0]} ${parts[1].charAt(0)}.`;
    return parts[0];
  };

  const getRankIcon = useCallback((rank) => {
    switch (rank) {
      case 1: return <Trophy className="w-6 h-6 text-yellow-500" />;
      case 2: return <Medal className="w-6 h-6 text-gray-400" />;
      case 3: return <Medal className="w-6 h-6 text-amber-600" />;
      default:
        return (
          <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center text-sm font-bold text-muted-foreground">
            {rank}
          </div>
        );
    }
  }, []);

  useEffect(() => {
    const fetchRankingData = async () => {
      setLoading(true);
      try {
        // 1. Get current user
        const user = await getCurrentUser();

        let currentProfile = null;
        if (user) {
          const { data: profileData, error: profileError } = await supabase
            .from('profiles')
            .select('id, full_name, total_score, total_verses_revealed, games_played, books_completed')
            .eq('id', user.id)
            .single();

          if (profileError && profileError.code !== 'PGRST116') {
            console.error('Error fetching user profile:', profileError);
          }
          currentProfile = profileData;
          setUserProfile(profileData);

          // Set display name
          const fullName = currentProfile?.full_name || user.user_metadata?.full_name;
          const email = user.email;
          const formatted = fullName
            ? formatName(fullName)
            : (email ? email.split('@')[0].substring(0, 10) : 'Jugador');
          setUserDisplayName(formatted);
        }

        // 2. Fetch top 50 players from DB
        const { data: topPlayers, error: topPlayersError } = await supabase
          .from('profiles')
          .select('id, full_name, total_score, total_verses_revealed')
          .order('total_score', { ascending: false })
          .order('total_verses_revealed', { ascending: false })
          .limit(50);

        if (topPlayersError) throw topPlayersError;

        // 3. Build players array — mark who is the current user
        let players = (topPlayers || []).map((p, index) => ({
          id: p.id,
          name: p.full_name ? formatName(p.full_name) : `Jugador ${index + 1}`,
          score: p.total_score || 0,
          verses: p.total_verses_revealed || 0,
          isUser: user ? p.id === user.id : false,
        }));

        // 4. If logged in, update current user's score with max(local, db)
        if (user) {
          const uScore = Math.max(localStats.totalScore, currentProfile?.total_score || 0);
          const uVerses = Math.max(localStats.totalVersesRevealed, currentProfile?.total_verses_revealed || 0);
          const fullName = currentProfile?.full_name || user.user_metadata?.full_name;
          const email = user.email;
          const uName = fullName
            ? formatName(fullName)
            : (email ? email.split('@')[0].substring(0, 10) : 'Jugador');

          const userInList = players.find(p => p.id === user.id);
          if (userInList) {
            // Update their score/verses with local max
            players = players.map(p =>
              p.id === user.id ? { ...p, name: uName, score: uScore, verses: uVerses } : p
            );
          } else {
            // Not returned by query (outside top 50), add them
            players.push({
              id: user.id,
              name: uName,
              score: uScore,
              verses: uVerses,
              isUser: true,
            });
          }
        }

        // 5. Sort by score desc, then verses desc
        players.sort((a, b) => b.score - a.score || b.verses - a.verses);

        // 6. Assign final ranks
        players = players.map((p, idx) => ({ ...p, rank: idx + 1 }));

        // 7. Build displayed list: top 7 naturally sorted, but always ensure current user is visible
        const top7 = players.slice(0, 7);
        const userInTop7 = top7.some(p => p.isUser);
        const userPlayer = players.find(p => p.isUser);

        let finalRanking;
        if (!userInTop7 && userPlayer) {
          // Show top 6 real players + user forced at slot 7 with real rank
          finalRanking = [...top7.slice(0, 6), userPlayer];
        } else {
          finalRanking = top7;
        }

        setLeaderboard(finalRanking);
      } catch (error) {
        console.error('Error fetching ranking data:', error);
        setLeaderboard([]);
      } finally {
        setLoading(false);
      }
    };

    fetchRankingData();
  }, [localStats.totalScore, localStats.totalVersesRevealed]);

  // PODIUM: user is always center (1st slot), top 2 non-user players fill 2nd and 3rd
  const userPlayer  = leaderboard.find(p => p.isUser) || null;
  const othersSorted = leaderboard.filter(p => !p.isUser); // already sorted by score
  const podiumFirst  = userPlayer;          // always the current user
  const podiumSecond = othersSorted[0] || null;  // best real player
  const podiumThird  = othersSorted[1] || null;  // second best real player

  const PodiumCard = ({ player, position }) => {
    if (!player) {
      return (
        <div className="text-center">
          <div className={`rounded-full mx-auto mb-2 flex items-center justify-center shadow-lg bg-gray-200 ${position === 1 ? 'w-20 h-20' : 'w-16 h-16'}`}>
            <span className="text-2xl font-bold text-gray-400">{position}</span>
          </div>
          <div className={`bg-card p-4 rounded-2xl border-2 border-dashed border-border flex flex-col justify-center items-center ${position === 1 ? 'h-40' : 'h-32'}`}>
            <p className="text-xs text-muted-foreground">Vacío</p>
            <p className="text-xs text-muted-foreground">0</p>
            <Badge variant="secondary" className="text-[9px] mt-1 flex flex-col leading-tight">
              <span className="font-bold">0</span>
              <span>versículos</span>
            </Badge>
          </div>
        </div>
      );
    }

    const podiumColors = {
      1: { circle: 'from-primary to-accent', card: 'from-primary/10 to-accent/10', border: 'border-primary', score: 'text-primary', badge: 'bg-primary text-primary-foreground' },
      2: { circle: 'from-gray-300 to-gray-400', card: 'from-card to-secondary/20', border: player.isUser ? 'border-primary' : 'border-gray-300', score: 'text-muted-foreground', badge: '' },
      3: { circle: 'from-amber-500 to-amber-600', card: 'from-card to-secondary/20', border: player.isUser ? 'border-primary' : 'border-amber-500', score: 'text-muted-foreground', badge: '' },
    };

    const c = podiumColors[position];

    return (
      <div className="text-center">
        <div className={`bg-gradient-to-br ${c.circle} rounded-full mx-auto mb-2 flex items-center justify-center shadow-lg ${position === 1 ? 'w-20 h-20 pulse-glow' : 'w-16 h-16'}`}>
          {position === 1
            ? <Trophy className="w-10 h-10 text-primary-foreground" />
            : <span className="text-2xl font-bold text-white">{position}</span>
          }
        </div>
        <div className={`bg-gradient-to-br ${c.card} p-4 rounded-2xl border-2 ${c.border} flex flex-col justify-center items-center overflow-hidden ${position === 1 ? 'h-40' : 'h-32'}`}>
          <p className={`font-semibold text-xs mb-1 break-words leading-tight px-1 ${position === 1 ? 'font-bold text-sm' : ''}`}>
            {player.name}
            {player.isUser && <span className="text-primary ml-1 text-[10px]">(Tú)</span>}
          </p>
          <p className={`text-xs mb-1 ${position === 1 ? 'text-sm font-bold' : ''} ${c.score}`}>
            {player.score.toLocaleString()}
          </p>
          {position === 1 ? (
            <Badge className={`${c.badge} text-[9px] mx-auto py-0.5 px-2 h-auto flex flex-col leading-tight`}>
              <span className="font-bold">{player.verses}</span>
              <span>versículos</span>
            </Badge>
          ) : (
            <Badge variant="secondary" className="text-[9px] mx-auto py-0.5 px-2 h-auto flex flex-col leading-tight">
              <span className="font-bold">{player.verses}</span>
              <span>versículos</span>
            </Badge>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-secondary/20 pb-24">
      <SideMenu open={menuOpen} onOpenChange={setMenuOpen} />

      {/* Header */}
      <header className="sticky top-0 bg-background/80 glass border-b border-border backdrop-blur-lg z-10">
        <div className="max-w-2xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setMenuOpen(true)}
              className="p-2 hover:bg-secondary rounded-full interactive"
            >
              <Menu className="w-6 h-6" />
            </button>
            <div>
              <h1 className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Clasificación
              </h1>
              <p className="text-sm text-muted-foreground">Top jugadores</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            <p className="mt-4 text-muted-foreground">Cargando clasificación...</p>
          </div>
        ) : (
          <>
            {/* Top 3 Podium — 2nd | 1st | 3rd */}
            <Card className="p-4 mb-8 shadow-md border-border">
              <div className="grid grid-cols-3 gap-3 items-end pt-4">
                <PodiumCard player={podiumSecond} position={2} />
                <PodiumCard player={podiumFirst}  position={1} />
                <PodiumCard player={podiumThird}  position={3} />
              </div>
            </Card>

            {/* Full Leaderboard */}
            <Card className="p-4">
              <h2 className="text-sm font-semibold mb-4 text-muted-foreground uppercase tracking-wide">
                Clasificación General (Top 7)
              </h2>
              <div className="space-y-2">
                {leaderboard.length === 0 && (
                  <p className="text-center text-muted-foreground py-6">
                    No hay jugadores registrados aún.
                  </p>
                )}
                {leaderboard.map((player) => (
                  <div
                    key={`${player.id}-${player.rank}`}
                    className={`flex items-center gap-4 p-3 rounded-xl interactive ${
                      player.isUser
                        ? 'bg-primary/10 border-2 border-primary'
                        : 'bg-secondary/50 hover:bg-secondary'
                    }`}
                  >
                    <div className="flex-shrink-0">
                      {player.isUser
                        ? <Trophy className="w-6 h-6 text-yellow-500" />
                        : getRankIcon(player.rank)
                      }
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`font-semibold truncate ${player.isUser ? 'text-primary' : ''}`}>
                        {player.name}
                        {player.isUser && (
                          <Badge variant="outline" className="ml-2 text-[10px] h-5 border-primary py-0">
                            Tú
                          </Badge>
                        )}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {player.isUser
                          ? `Puesto #${player.rank} global · ${player.verses} versículos`
                          : `${player.verses} versículos revelados`
                        }
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-lg">{player.score.toLocaleString()}</p>
                      <p className="text-xs text-muted-foreground">puntos</p>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </>
        )}

        {/* Your Stats */}
        <Card className="mt-6 p-6 border-2 border-primary/20 shadow-xl bg-card/30 backdrop-blur-sm">
          <h2 className="text-[11px] md:text-sm font-bold mb-6 text-foreground uppercase tracking-wider flex items-center justify-center gap-2 whitespace-nowrap">
            <Award className="w-4 h-4 md:w-5 md:h-5 text-primary" />
            Tus Estadísticas Globales
          </h2>
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-gradient-to-br from-white to-gray-200 border border-gray-300 rounded-2xl p-4 shadow-[inset_0_2px_4px_rgba(255,255,255,0.8),0_4px_8px_rgba(0,0,0,0.1)] flex flex-col items-center justify-center transition-transform hover:scale-[1.02]">
              <p className="text-[10px] font-black uppercase text-gray-500 mb-2 text-center leading-tight h-6 flex items-center">Partidas Jugadas</p>
              <p className="text-xl font-bold text-black">
                {Math.max(userProfile?.games_played || 0, localStats.gamesPlayed)}
              </p>
            </div>

            <div className="bg-gradient-to-br from-primary/30 to-primary/10 border border-primary/30 rounded-2xl p-4 shadow-[inset_0_2px_4px_rgba(255,255,255,0.6),0_4px_8px_rgba(0,0,0,0.1)] flex flex-col items-center justify-center transition-transform hover:scale-[1.02]">
              <p className="text-[10px] font-black uppercase text-gray-600 mb-2 text-center leading-tight h-6 flex items-center">Versículos Revelados</p>
              <p className="text-xl font-bold text-black">
                {Math.max(userProfile?.total_verses_revealed || 0, localStats.totalVersesRevealed)}
              </p>
            </div>

            <div className="bg-gradient-to-br from-accent/30 to-accent/10 border border-accent/30 rounded-2xl p-4 shadow-[inset_0_2px_4px_rgba(255,255,255,0.6),0_4px_8px_rgba(0,0,0,0.1)] flex flex-col items-center justify-center transition-transform hover:scale-[1.02]">
              <p className="text-[10px] font-black uppercase text-gray-600 mb-2 text-center leading-tight h-6 flex items-center">Libros Completados</p>
              <p className="text-xl font-bold text-black">
                {Math.max(
                  Array.isArray(userProfile?.books_completed) ? userProfile.books_completed.length : 0,
                  completedBooksCount
                )}/66
              </p>
            </div>

            <div className="bg-gradient-to-br from-yellow-100 to-yellow-200 border border-yellow-300 rounded-2xl p-4 shadow-[inset_0_2px_4px_rgba(255,255,255,0.8),0_4px_8px_rgba(0,0,0,0.1)] flex flex-col items-center justify-center transition-transform hover:scale-[1.02]">
              <p className="text-[10px] font-black uppercase text-gray-600 mb-2 text-center leading-tight h-6 flex items-center">Puntuación Total</p>
              <p className="text-xl font-bold text-black">
                {Math.max(userProfile?.total_score || 0, localStats.totalScore).toLocaleString()}
              </p>
            </div>
          </div>
        </Card>

        {/* Play Button CTA */}
        <div className="mt-8 mb-4 px-2">
          <Button
            onClick={handlePlayClick}
            size="lg"
            className="w-full h-16 text-lg font-bold bg-primary hover:bg-primary/90 text-primary-foreground rounded-full shadow-lg pulse-glow"
          >
            <span className="mr-3 text-xl">▶</span>
            JUGAR
          </Button>
        </div>
      </main>

      {/* Bottom Navigation */}
      <nav className="fixed bottom-0 left-0 right-0 bg-card border-t border-border shadow-2xl">
        <div className="max-w-2xl mx-auto px-4 py-4 grid grid-cols-4 gap-2">
          <button
            onClick={() => navigate('/')}
            className="flex flex-col items-center gap-1 p-2 rounded-lg hover:bg-secondary interactive"
          >
            <div className="w-5 h-5 bg-muted-foreground rounded"></div>
            <span className="text-xs text-muted-foreground">Inicio</span>
          </button>
          <button
            onClick={() => navigate('/collection')}
            className="flex flex-col items-center gap-1 p-2 rounded-lg hover:bg-secondary interactive"
          >
            <svg className="w-5 h-5 text-muted-foreground" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
              <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
            </svg>
            <span className="text-xs text-muted-foreground">Colección</span>
          </button>
          <button
            onClick={() => navigate('/ranking')}
            className="flex flex-col items-center gap-1 p-2 rounded-lg bg-primary text-primary-foreground"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="12" y1="20" x2="12" y2="10" />
              <line x1="18" y1="20" x2="18" y2="4" />
              <line x1="6" y1="20" x2="6" y2="16" />
            </svg>
            <span className="text-xs font-semibold">Ranking</span>
          </button>
          <button
            onClick={() => navigate('/profile')}
            className="flex flex-col items-center gap-1 p-2 rounded-lg hover:bg-secondary interactive"
          >
            <svg className="w-5 h-5 text-muted-foreground" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
              <circle cx="12" cy="7" r="4" />
            </svg>
            <span className="text-xs text-muted-foreground">Perfil</span>
          </button>
        </div>
      </nav>
    </div>
  );
};